<section class="page">
  <header class="page-header">
    <div>
      <h1>Points</h1>
      <p>Platform balances, points history, and manual adjustments</p>
    </div>
  </header>

  <div class="tabs">
    <button type="button" class="tab" [class.active]="tab() === 'users'" (click)="setTab('users')">
      User balances
    </button>
    <button type="button" class="tab" [class.active]="tab() === 'history'" (click)="setTab('history')">
      History
    </button>
  </div>

  @if (error()) {
    <div class="alert alert-error">{{ error() }}</div>
  }
  @if (success()) {
    <div class="alert alert-success">{{ success() }}</div>
  }

  @if (adjustUser(); as adj) {
    <div class="panel adjust-box">
      <h2>Adjust points — #{{ adj.id }} {{ fullName(adj) }}</h2>
      <p class="muted">Current balance {{ n(adj.points_balance) }}. Positive credit / debit cannot go below 0.</p>
      <div class="filters">
        <input type="number" min="1" step="1" [(ngModel)]="adjustAmount" />
        <select [(ngModel)]="adjustDirection">
          <option value="credit">Credit (add)</option>
          <option value="debit">Debit (subtract)</option>
        </select>
        <input
          type="text"
          placeholder="Reason (required)"
          [(ngModel)]="adjustReason"
          maxlength="255"
        />
        <button
          class="btn btn-primary"
          type="button"
          (click)="confirmAdjust()"
          [disabled]="adjusting() || signedPoints() === 0 || !adjustReason.trim()"
        >
          {{ adjusting() ? 'Saving…' : 'Apply' }}
        </button>
        <button class="btn btn-secondary" type="button" (click)="cancelAdjust()" [disabled]="adjusting()">
          Cancel
        </button>
      </div>
    </div>
  }

  @if (tab() === 'users') {
    @if (totals(); as t) {
      <div class="stats">
        <div class="stat">
          <div class="stat-label">Total balance</div>
          <div class="stat-value">{{ t.total_points_balance }}</div>
        </div>
        <div class="stat">
          <div class="stat-label">Total earned</div>
          <div class="stat-value">{{ t.total_points_earned }}</div>
        </div>
        <div class="stat">
          <div class="stat-label">Total redeemed</div>
          <div class="stat-value">{{ t.total_points_redeemed }}</div>
        </div>
        <div class="stat">
          <div class="stat-label">Users with balance</div>
          <div class="stat-value">{{ t.users_with_balance }} / {{ t.users_count }}</div>
        </div>
      </div>
    }

    <div class="filters panel">
      <input
        type="search"
        placeholder="Search email, name, phone, id"
        [(ngModel)]="searchQ"
        (keyup.enter)="searchUsers()"
      />
      <button class="btn btn-secondary" type="button" (click)="searchUsers()" [disabled]="loading()">
        Search
      </button>
    </div>

    <div class="panel">
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Balance</th>
              <th>Earned</th>
              <th>Redeemed</th>
              <th>Claim AUD</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            @if (loading()) {
              <tr><td colspan="7" class="muted">Loading…</td></tr>
            } @else if (!userRows().length) {
              <tr><td colspan="7" class="muted">No users found</td></tr>
            } @else {
              @for (row of userRows(); track row.id) {
                <tr class="clickable" (click)="openUser(row.id)">
                  <td>#{{ row.id }} {{ fullName(row) }}</td>
                  <td>{{ row.email || '—' }}</td>
                  <td>{{ n(row.points_balance) }}</td>
                  <td>{{ n(row.total_points_earned) }}</td>
                  <td>{{ n(row.total_points_redeemed) }}</td>
                  <td>{{ row.total_points_claim_amount ?? 0 }}</td>
                  <td>
                    <button class="btn btn-secondary" type="button" (click)="openAdjust(row, $event)">
                      Adjust
                    </button>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>
      <div class="pager">
        <span class="muted">Page {{ userPage() }} / {{ userPages() }} · {{ userTotal() }} total</span>
        <div class="pager-actions">
          <button class="btn btn-secondary" type="button" [disabled]="userPage() <= 1 || loading()" (click)="loadUsers(userPage() - 1)">Prev</button>
          <button class="btn btn-secondary" type="button" [disabled]="userPage() >= userPages() || loading()" (click)="loadUsers(userPage() + 1)">Next</button>
        </div>
      </div>
    </div>
  } @else {
    <div class="filters panel">
      <input type="number" placeholder="User ID" [(ngModel)]="historyUserId" />
      <select [(ngModel)]="historyType">
        <option value="">All types</option>
        <option value="earn">Earn</option>
        <option value="redeem">Redeem</option>
        <option value="adjust">Adjust</option>
      </select>
      <button class="btn btn-secondary" type="button" (click)="filterHistory()" [disabled]="loading()">
        Filter
      </button>
    </div>

    <div class="panel">
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>User</th>
              <th>Type</th>
              <th>Points</th>
              <th>Amount AUD</th>
              <th>Balance after</th>
              <th>Description</th>
              <th>When</th>
            </tr>
          </thead>
          <tbody>
            @if (loading()) {
              <tr><td colspan="8" class="muted">Loading…</td></tr>
            } @else if (!historyRows().length) {
              <tr><td colspan="8" class="muted">No point transactions</td></tr>
            } @else {
              @for (row of historyRows(); track row.id) {
                <tr>
                  <td>{{ row.id }}</td>
                  <td class="clickable" (click)="openUser(row.user_id)">
                    #{{ row.user_id }}
                    <div class="muted small">{{ row.user?.email || fullName(row.user) }}</div>
                  </td>
                  <td><span class="badge" [attr.data-status]="row.type">{{ row.type }}</span></td>
                  <td>{{ row.points }}</td>
                  <td>{{ row.amount_aud ?? '—' }}</td>
                  <td>{{ row.balance_after ?? '—' }}</td>
                  <td>{{ row.description || row.reference || '—' }}</td>
                  <td>{{ row.created_at ? (row.created_at | date: 'medium') : '—' }}</td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>
      <div class="pager">
        <span class="muted">Page {{ historyPage() }} / {{ historyPages() }} · {{ historyTotal() }} total</span>
        <div class="pager-actions">
          <button class="btn btn-secondary" type="button" [disabled]="historyPage() <= 1 || loading()" (click)="loadHistory(historyPage() - 1)">Prev</button>
          <button class="btn btn-secondary" type="button" [disabled]="historyPage() >= historyPages() || loading()" (click)="loadHistory(historyPage() + 1)">Next</button>
        </div>
      </div>
    </div>
  }
</section>
